import React, { useMemo, useEffect, useState } from "react";
import { getUrlParam, removeUrlParam } from "helpers/url-params";
import Pagination from "components/pagination";
import ProductItem from "components/product/item";
import { useFilters, useCustomer, useSettings } from "hooks/useStore";
import { sortProducts, filterProducts } from "utils/filter-sort";
import Sorting from "./sorting";
import { trackViewItemList } from "analytics";
import PromoCard from "./promo-card";
import { useMediaQuery } from "react-responsive";
import { SCREEN_SIZES } from "utils/constants";
import { isExperienceActive } from "helpers/convert";
import ProductsListSkeleton from "components/loader/products-list-skeleton";
import PaginationRange from "./pagination-range";

const pageSize = 12;

const ProductsList = ({ collection, secureCheckout, promoCard }) => {
	const { handle, title, products, defaultSort, thirdImgAsPrimary = false } = collection;
	const [currentPage, setCurrentPage] = useState(1);
	const { collections, updatePageDetails } = useFilters(["collections", "updatePageDetails"]);
	const customer = useCustomer();
	const { pageLoading } = useSettings(["pageLoading"]);

	const [activePromoCard, setActivePromoCard] = useState(false);

	useEffect(() => {
		if (!promoCard?.enabled) return;

		// Check if the promo card is active, timeout to prevent flickering
		const timeout = setTimeout(() => {
			const isActive = isExperienceActive(["v.1004320248-g"]);
			setActivePromoCard(isActive);
		}, 1);

		return () => clearTimeout(timeout);
	}, [promoCard]);

	const collectionFilters = collections[handle];
	const sortBy = collectionFilters?.sortBy || defaultSort || "best-selling";

	const productResults = useMemo(() => {
		const filteredProducts = collectionFilters
			? filterProducts({ products, collectionFilters, matchAllFilters: true })
			: products;
		return sortProducts(filteredProducts, sortBy);
	}, [collectionFilters, sortBy, products]);


	const isMobile = useMediaQuery({ maxWidth: SCREEN_SIZES.md });

	const paginatedItems = useMemo(() => {
		const allProducts = [...productResults];

		// Insert promo card logic
		if (activePromoCard && allProducts.length > 12) {
			allProducts.splice(isMobile ? 3 : 5, 0, { promoCard: true });
		}

		// Paginate the products
		const startIndex = (currentPage - 1) * pageSize;
		return allProducts.slice(startIndex, startIndex + pageSize);
	}, [productResults, activePromoCard, currentPage, isMobile]);

	useEffect(() => {
		const page = +getUrlParam("page");
		page && setCurrentPage(page <= Math.ceil(productResults.length / pageSize) ? page : 1);
	}, []);

	useEffect(() => {
		const visibleProducts = paginatedItems.filter((item) => !item.promoCard);
		trackViewItemList(visibleProducts, { title, handle: `/collections/${handle}` }, customer);
		updatePageDetails(handle, currentPage, pageSize, productResults.length, activePromoCard);
	}, [currentPage]);

	const { priceRange, tags, totalActiveFilters } = collectionFilters || {};

	useEffect(() => {
		if (currentPage === 1) return;

		setCurrentPage(1);
		removeUrlParam("page");
		updatePageDetails(handle, currentPage, pageSize, productResults.length, activePromoCard);
	}, [priceRange, tags, sortBy, totalActiveFilters]);

	if (pageLoading) return <ProductsListSkeleton />;

	return (
		<>
			<div className="flex justify-between items-center md:border-b border-solid pb-1 md:pb-6 my-4 collection-v2-header">
				<div className="collection-v1">
					<PaginationRange handle={handle} />
				</div>
				<div className="flex items-center gap-3 collection-v1">
					<Sorting handle={handle} value={sortBy} />
				</div>
			</div>
			<div className="flex flex-wrap gap-x-3.5 xs:gap-x-3 lg:gap-x-4 gap-y-10">
				{paginatedItems.length > 0 ? (
					paginatedItems.map((item, index) =>
						item.promoCard ? (
							<PromoCard
								key={`promo-${index}`}
								className="w-[calc(50%-7px)] md:w-[calc(33%-7px)] lg:w-[calc(33%-10px)]"
								data={promoCard}
							/>
						) : (
							<ProductItem
								index={index + 1}
								key={item.handle}
								product={item}
								secureCheckout={secureCheckout}
								thirdImgAsPrimary={thirdImgAsPrimary}
								className="w-[calc(50%-7px)] md:w-[calc(33%-7px)] lg:w-[calc(33%-10px)] flex h-full"
							/>
						)
					)
				) : <h2>Results not found</h2>
				}
			</div>

			<Pagination
				currentPage={currentPage}
				totalCount={productResults.length}
				pageSize={pageSize}
				onPageChange={(page) => setCurrentPage(page)}
			/>
		</>
	);
};

export default ProductsList;